import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import Icon from '../../components/Icon';
import type { AdminThemePalette } from '../../games/types';

import { widgetFor } from './widgetSchema';
import { WidgetRenderer } from './widgets';
import ExplainedSetting from './ExplainedSetting';
import { targetProfitPctProps, targetProfitEnabledProps } from './targetProfitPresets';
import { descriptionFor, tr } from './translations';
import { useAdminLang } from './AdminLangContext';
import { isTtsSupported, onTtsStateChange, speak, stopTts } from '../../lib/tts';

export type ConfigMeta = {
  key: string;
  category: string;
  description: string;       // fallback (English) from the server manifest
  live: boolean;
};

export type ConfigRow = {
  meta: ConfigMeta;
  value: unknown;
};

type Props = {
  row: ConfigRow;
  theme: AdminThemePalette;
  game: string;
  onSave: (key: string, value: unknown) => Promise<void>;
};

function iconFor(key: string): string {
  if (/AFK|QUIT/.test(key)) return 'person_off';
  if (/BOT/.test(key)) return 'smart_toy';
  if (/VOICE|MIC|AUDIO/.test(key)) return 'mic';
  if (/TIMER|SEC|MS|DELAY|TIMEOUT|COUNTDOWN/.test(key)) return 'schedule';
  if (/PRIZE|FEE|BET|WALLET|COIN|STAKE|PAYOUT/.test(key)) return 'paid';
  if (/STRIKE|KICK/.test(key)) return 'gavel';
  if (/WIN|BRUTALITY|SMART|DIFFICULTY|HARD|EASY/.test(key)) return 'psychology';
  if (/ROLL|DICE/.test(key)) return 'casino';
  if (/BOARD|CELL|TOKEN|PIECE/.test(key)) return 'grid_view';
  if (/NAME|AVATAR/.test(key)) return 'face';
  return 'tune';
}

// Human-readable title synthesised from the all-caps key name.
function prettyLabel(key: string): string {
  return key
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\bafk\b/g, 'idle')
    .replace(/\bms\b/g, '(ms)')
    .replace(/\bsec\b/g, '(seconds)')
    .replace(/^./, (c) => c.toUpperCase());
}

export default function ConfigField({ row, theme, game, onSave }: Props) {
  const t = theme;
  const { lang } = useAdminLang();
  const widget = widgetFor(row.meta.key);
  const isReadonly = widget.kind === 'readonly';
  // P3 (2026-08-07): the two Target-Profit keys render as self-documenting
  // ExplainedSetting preset controls instead of a bare slider / toggle.
  const isTpPct = row.meta.key === 'target_profit_pct';
  const isTpEnabled = row.meta.key === 'target_profit_enabled';
  const isTP = isTpPct || isTpEnabled;
  const [draft, setDraft] = useState<unknown>(row.value);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Description is hidden by default so the panel stays compact — the
  // admin opens the description only when they want to read it. The
  // read-aloud button works whether or not the text is visible.
  const [showDesc, setShowDesc] = useState(false);
  const [mySpeaking, setMySpeaking] = useState(false);

  useEffect(() => { setDraft(row.value); }, [row.value]);

  // A single speechSynthesis instance is shared across all rows.
  // We subscribe to its state and only light up THIS row's icon when
  // the last speak() originated here — tracked via a local flag that
  // is set when this row clicks speak and cleared when speech ends.
  useEffect(() => onTtsStateChange((s) => { if (!s) setMySpeaking(false); }), []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(row.value);
  const description = descriptionFor(game, row.meta.key, lang, row.meta.description);
  const title = prettyLabel(row.meta.key);

  async function save() {
    if (!dirty || isReadonly) return;
    // A negative target = a deliberate promotional house LOSS. The generic
    // Save path has no confirm dialog, so gate it here (mirrors the master
    // casino-profit control's loss warning).
    if (isTpPct && Number(draft) < 0 &&
        !window.confirm(`This runs this game at a deliberate house LOSS of about ${Math.abs(Number(draft))}%. Players will net-win — the house is spending. Continue?`)) {
      return;
    }
    setSaving(true); setError(null);
    try {
      await onSave(row.meta.key, draft);
      setSavedAt(Date.now());
    } catch (e) {
      setError((e as Error).message || 'Save failed');
    } finally { setSaving(false); }
  }
  function revert() { setDraft(row.value); setError(null); }

  function handleSpeak() {
    if (mySpeaking) {
      stopTts();
      setMySpeaking(false);
      return;
    }
    // Prefix with the pretty title so listeners know which setting is being read.
    const text = `${title}. ${description}`;
    if (speak(text, lang)) setMySpeaking(true);
  }

  const ttsAvailable = isTtsSupported();

  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 hover:border-neutral-300 dark:border-neutral-700 transition">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${t.softBg} shrink-0`}>
          <Icon name={iconFor(row.meta.key)} size={18} className="text-neutral-700 dark:text-neutral-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {!isTP && <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{title}</span>}
            {row.meta.live && !isReadonly && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border border-emerald-600/30">
                {tr('live', lang)}
              </span>
            )}
            {!row.meta.live && !isReadonly && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-600/20 text-amber-700 dark:text-amber-300 border border-amber-600/30">
                {tr('restartRequired', lang)}
              </span>
            )}
            {isReadonly && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-600/20 text-neutral-700 dark:text-neutral-300 border border-neutral-600/30">
                {tr('readonlyBadge', lang)}
              </span>
            )}
          </div>
          <code className="block mt-0.5 text-[10px] font-mono text-neutral-500 break-all">{row.meta.key}</code>

          {/* Row of icon buttons under the title — always visible, so the
              admin can read aloud or expand the description without
              scrolling to find the widget first. */}
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => setShowDesc((v) => !v)}
              disabled={!description}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold
                         bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200
                         disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <Icon name={showDesc ? 'visibility_off' : 'visibility'} size={14} />
              {showDesc ? tr('hideDescription', lang) : tr('showDescription', lang)}
            </button>
            <button
              type="button"
              onClick={handleSpeak}
              disabled={!ttsAvailable || !description}
              title={!ttsAvailable ? tr('ttsUnsupported', lang) : undefined}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold
                          transition disabled:opacity-40 disabled:cursor-not-allowed
                          ${mySpeaking
                            ? `${t.accent} ${t.accentHover} text-white`
                            : 'bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200'}`}
            >
              <Icon name={mySpeaking ? 'stop_circle' : 'volume_up'}
                    size={14} filled={mySpeaking} />
              {mySpeaking ? tr('stopReading', lang) : tr('readAloud', lang)}
            </button>
            <span className="text-[10px] text-neutral-500 ml-auto">
              {lang === 'hi' ? 'भाषा:' : 'Lang:'} <b>{lang === 'hi' ? 'हिन्दी' : 'EN'}</b>
            </span>
          </div>

          <AnimatePresence initial={false}>
            {showDesc && (
              <motion.p
                key="desc"
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.18 }}
                className="text-xs text-neutral-400 leading-relaxed whitespace-pre-wrap overflow-hidden"
                lang={lang}
              >
                {description || tr('noDescription', lang)}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-3 pl-12">
        {isTpPct ? (
          <ExplainedSetting {...targetProfitPctProps(draft, setDraft, { allowNegative: true, game })} />
        ) : isTpEnabled ? (
          <ExplainedSetting {...targetProfitEnabledProps(draft, setDraft)} />
        ) : (
          <WidgetRenderer widget={widget} value={draft} onChange={setDraft} theme={theme} />
        )}
      </div>

      {!isReadonly && (
        <div className="mt-3 pl-12 flex items-center gap-2 flex-wrap">
          <button
            onClick={save}
            disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white
                        ${t.accent} ${t.accentHover}
                        disabled:opacity-40 disabled:cursor-not-allowed transition`}
          >
            {saving ? <Icon name="progress_activity" size={16} className="animate-spin" /> : <Icon name="save" size={16} />}
            {saving ? tr('saving', lang) : tr('save', lang)}
          </button>
          {dirty && !saving && (
            <button onClick={revert}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm
                         bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200 transition">
              <Icon name="undo" size={16} /> {tr('undo', lang)}
            </button>
          )}
          {error && (
            <span className="text-xs text-red-400 inline-flex items-center gap-1">
              <Icon name="error" size={14} /> {error}
            </span>
          )}
          {!dirty && !error && savedAt && Date.now() - savedAt < 4000 && (
            <span className="text-xs text-emerald-400 inline-flex items-center gap-1">
              <Icon name="check_circle" size={14} filled /> {tr('savedApplied', lang)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
